import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal, notification, Space } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useForm } from "antd/es/form/Form";
import usePriceFormulas from "@/hooks/usePriceFormulas";
import { PriceFormula } from "@/root/models/priceFormula";
import { CreatePriceFormulaPayload } from "@/api/admin/priceFormulas";
import PriceFormulaForm from "./PriceFormulaForm";

const { confirm } = Modal;

interface TableActionsProps {
  priceFormula: PriceFormula;
  modal: string | null;
  onCancel: () => void;
  onModalOpen: () => void;
  onEditSuccess: () => void;
}

const PriceFormulaTableActions = ({
  priceFormula,
  modal,
  onCancel,
  onEditSuccess,
  onModalOpen,
}: TableActionsProps) => {
  const { t } = useTranslation();
  const [form] = useForm();

  const onDeleteError = (message: string) => {
    notification.error({
      message,
    });
  };

  const {
    deletePriceFormula,
    deleteLoading,
    editPriceFormula,
    editLoading,
    editError,
  } = usePriceFormulas({ onDeleteError, onEditSuccess });

  const handleDelete = async () => {
    await deletePriceFormula(priceFormula.priceFormulaUUID);
  };

  const handleEdit = async (values: CreatePriceFormulaPayload) => {
    editPriceFormula(priceFormula.priceFormulaUUID, values);
  };

  const submit = () => {
    form.submit();
  };

  const onHandleOpen = () => {
    onModalOpen();
  };

  useEffect(() => {
    form.setFieldsValue(priceFormula);
  }, [modal]);

  const handleDeleteButtonClick = () => {
    confirm({
      title: t("Підтвердити видалення"),
      okText: t("Видалити"),
      okType: "danger",
      cancelText: t("Скасувати"),
      onOk: handleDelete,
    });
  };

  return (
    <Space className="table-actions" size="middle">
      <Button size="small" icon={<EditOutlined />} onClick={onHandleOpen} />
      <Button
        size="small"
        icon={<DeleteOutlined />}
        loading={deleteLoading}
        danger
        onClick={handleDeleteButtonClick}
      />
      <Modal
        maskClosable={false}
        okText={t("Зберегти")}
        title={
          <div style={{ marginBottom: 20 }}>
            {t("Редагування цінової формули")}
          </div>
        }
        open={modal === priceFormula.priceFormulaUUID}
        onCancel={onCancel}
        onOk={submit}
        okButtonProps={{ loading: editLoading }}
      >
        <PriceFormulaForm
          form={form}
          handleSubmit={handleEdit}
          error={editError}
        />
      </Modal>
    </Space>
  );
};

export default PriceFormulaTableActions;
